import React, { useMemo } from 'react';
import classnames from 'classnames';

import { can } from '../can';
import { routes } from '../routes';
import { getCountryName } from '../utils/index';

import api from '../utils/api';
import { useQuery } from '../utils/query';

import { lockerStatusLabels, lockerStatusColors, lockerConnectionLabels, doorTypeLabels } from '../constants';

import PageHeader from '../components/common/PageHeader';
import Pagination from '../components/common/Pagination';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Link from '../components/common/Link';

import { List, Header, Body, Row, Cell } from '../components/common/List';
import { modeLabels } from '../constants';

import { Loading } from './PageStatus';

import css from './Lockers.css';
import { FiShuffle } from 'react-icons/fi';


export const Lockers = (props) => {
  const query = useQuery(props.location, props.history);
  const { user } = api.auth();
  const lockerIndexQuery = useMemo(() => {
    const currentQuery = { ...query.obj };
    const search = typeof currentQuery.search === 'string' ? currentQuery.search.trim() : '';

    if (!search || search.length < 3) {
      delete currentQuery.search;
    } else {
      currentQuery.search = search;
    }

    return currentQuery;
  }, [query.obj]);

  const { index, actions } = api.lockers.index(lockerIndexQuery);
  return (
    <main className={css.root}>
      <PageHeader title="Lockers">
        {can(user, 'create', 'lockers') && (
          <Button noMargin to={routes('newLocker')}>
            Nuevo
          </Button>
        )}
      </PageHeader>
      <div className={css.toolBar}>
        <Input
          className={css.search}
          label="Buscar por nombre:"
          name="search"
          onChange={query.set}
          value={query.get('search')}
          note="Ingresa al menos 3 caracteres"
          placeholder="Nombre del locker"
        />
        <Toggle
          label="Solo lockers activos"
          onChange={(n, chk) => query.set(n, chk ? 'active' : null)}
          name={'status'}
          value={query.get('status') || false}
        />
        <Toggle
          label="Solo lockers online"
          onChange={(n, chk) => query.set(n, chk ? true : false)}
          name={'online'}
          value={query.get('online') == 'true' || query.get('online') || false}
        />
      </div>
      {index.loaded ? (
        <List className={css.root}>
          <Header>
            <Cell head>ID</Cell>
            <Cell head>Nombre</Cell>
            <Cell head>Setup</Cell>
            <Cell head>Versión</Cell>
            <Cell head>Ubicación</Cell>
            <Cell head>País</Cell>
            <Cell head>Puertas</Cell>
            <Cell head>Estado</Cell>
          </Header>
          <Body>
            {index.data.toJS().map((locker, index) => {
              const to = routes('locker', { id: locker.id });
              const isUnregistered = locker.status == 'unregistered';
              const prefs = locker.preferences || {};
              const country = getCountryName(locker.locationCountryCode);
              const doors = locker.doorsInfo.find((r) => r.status == 'active') || {};
              const doorsAvailable = doors.doorsTotal - doors.doorsReserved - doors.doorsOccupied;
              const modeLabel = modeLabels(prefs.mode) || modeLabels('normal');
              const doorTypeLabel = doorTypeLabels(prefs.doorType) || doorTypeLabels('serial');
              const lockerVersion = locker.extras?.version;
              const hasLockerVersion = !!lockerVersion;
              const displayLockerVersion = lockerVersion || '0.1.1';
              const connectionStatus = isUnregistered ? 'unregistered' : locker.online ? 'online' : 'offline';
              const connectionLabel = lockerConnectionLabels(connectionStatus);
              const isInactiveLocker = locker.status !== 'active';

              let incongruencesCheckIcon = null;
              if (prefs.enableIncongruencesCheck && user.role === 'admin') {
                incongruencesCheckIcon = (
                  <span
                    className={classnames(css.incongruencesCheckIcon)}
                    title="Chequear incongruencias activado"
                  >
                    <FiShuffle />
                  </span>
                );
              }

              return (
                <Row key={`lockers-row-${index}`}>
                  <Cell>
                    {locker.id}
                  </Cell>
                  <Cell className={css.name}>
                    <div className={css.nameWrap}>
                      <span
                        className={classnames(css.connectionDot, {
                          [css.connectionDotOnline]: !isInactiveLocker && connectionStatus === 'online',
                          [css.connectionDotOffline]: !isInactiveLocker && (connectionStatus === 'offline' || connectionStatus === 'unregistered'),
                          [css.connectionDotInactive]: isInactiveLocker,
                        })}
                        title={isInactiveLocker ? 'Locker inactivo' : `Estado conexión: ${connectionLabel}`}
                      />
                      <Link to={to} className={css.nameLink}>{locker.name}</Link>
                    </div>
                    &nbsp;
                    {incongruencesCheckIcon}
                  </Cell>
                  <Cell className={css.modeTypeCell}>
                    <div className={css.modeTypeStack}>
                      <span
                        className={classnames(css.modeBadge, {
                          [css.modeBadgeHousing]: prefs.mode === 'housing',
                          [css.modeBadgeCustody]: prefs.mode === 'custody',
                          [css.modeBadgeNormal]: prefs.mode !== 'housing' && prefs.mode !== 'custody',
                        })}
                        title={modeLabels(prefs.mode) || modeLabels('normal')}
                      >
                        {modeLabel}
                      </span>
                      <span className={css.typeBadge} title={doorTypeLabel}>
                        {doorTypeLabel}
                      </span>
                      {prefs.enableDualLocker && (
                        <span className={css.dualBadge} title="Dual locker habilitado">
                          Dual
                        </span>
                      )}
                      {prefs.isMedical && (
                        <span className={css.clinicalBadge} title="Locker configurado para farmacia">
                          Farmacia
                        </span>
                      )}
                    </div>
                  </Cell>
                  <Cell className={css.versionCell}>
                    <span
                      className={classnames(css.versionLabel, {
                        [css.versionLabelEnabled]: hasLockerVersion,
                        [css.versionLabelDisabled]: !hasLockerVersion,
                      })}
                      title={
                        hasLockerVersion
                          ? `Versión reportada por el locker: ${displayLockerVersion}`
                          : 'Sin versión reportada. Se muestra versión por defecto 0.1.1'
                      }
                    >
                      v{displayLockerVersion}
                    </span>
                  </Cell>
                  <Cell>
                    {user.role === 'admin' && locker.locationId ? (
                      <Link to={routes('location', { id: locker.locationId })}>{locker.locationName}</Link>
                    ) : (
                      locker.locationName
                    )}
                  </Cell>
                  <Cell>{country}</Cell>
                  <Cell>
                    {doors.doorsTotal ? (
                      <>
                        <small>
                          {doorsAvailable}/{doors.doorsTotal} Disponibles
                        </small>
                        <div
                          className={css.usageBar}
                          style={{
                            width: 100, marginBottom: 5,
                          }}>
                          <div
                            className={css.inner}
                            style={{
                              width: (100 * doorsAvailable) / doors.doorsTotal,
                            }}
                          />
                        </div> 
                      </>
                    ) : (
                      <small>{'Sin puertas creadas'}</small>
                    )}
                  </Cell>
                  <Cell
                    style={{
                      width: 100,
                      color: lockerStatusColors(locker.status),
                    }}
                    className={css.status}>
                    {lockerStatusLabels(locker.status)}
                  </Cell>
                </Row>
              );
            })}
          </Body>
        </List>
      ) : index.loading ? (
        <Loading compact />
      ) : (
        <p>{index.error}</p>
      )}
      {index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}


    </main>
  );
};

export default Lockers;
